import Phaser from 'phaser';
import { createTutorialHintDom, destroyTutorialHint } from '../tutorial/ui';

type Rect = { x: number; y: number; w: number; h: number };

export interface BoardViewportAdapter {
  getBaseRectBox: () => Rect | null;
  getRectBox: () => Rect | null;
  hasDraggablePieceAtScreenPoint: (x: number, y: number) => boolean;
  setViewportTransform: (scale: number, offsetX: number, offsetY: number) => void;
}

export interface BoardViewportControllerOptions {
  scene: Phaser.Scene;
  canvas: HTMLCanvasElement;
  adapter: BoardViewportAdapter;
  minScale?: number;
  maxScale?: number;
  onZoomPercentChange?: (percent: number) => void;
}

type PointerInfo = { x: number; y: number };

/**
 * Управляет zoom/pan только для viewport доски.
 * Не меняет игровую логику снапа/склейки; только трансформацию рендера.
 */
export class BoardViewportController {
  private scene: Phaser.Scene;
  private canvas: HTMLCanvasElement;
  private adapter: BoardViewportAdapter;
  private onZoomPercentChange?: (percent: number) => void;
  private readonly maxScale: number;
  private minScale: number;
  private fitScale = 1;

  private scale = 1;
  private offsetX = 0;
  private offsetY = 0;
  private readonly clampMargin = 0;

  private pointers = new Map<number, PointerInfo>();
  private pinchStartDistance = 0;
  private pinchStartScale = 1;
  private pinchActive = false;
  private panningBySpace = false;
  private spaceDown = false;
  private panLastX = 0;
  private panLastY = 0;
  private pointerDownForPan = false;
  private singleFingerPanActive = false;
  private singleFingerPanPointerId: number | null = null;
  private pieceDragging = false;
  private lastReportedZoomPercent = -1;

  private readonly onWheelBound: (event: WheelEvent) => void;
  private readonly onPointerDownBound: (event: PointerEvent) => void;
  private readonly onPointerMoveBound: (event: PointerEvent) => void;
  private readonly onPointerUpBound: (event: PointerEvent) => void;
  private readonly onPointerCancelBound: (event: PointerEvent) => void;
  private readonly onKeyDownBound: (event: KeyboardEvent) => void;
  private readonly onKeyUpBound: (event: KeyboardEvent) => void;
  private previousTouchAction = '';

  constructor(options: BoardViewportControllerOptions) {
    this.scene = options.scene;
    this.canvas = options.canvas;
    this.adapter = options.adapter;
    this.onZoomPercentChange = options.onZoomPercentChange;
    this.maxScale = options.maxScale ?? 2.5;
    this.minScale = options.minScale ?? 0.6;

    this.onWheelBound = (event) => this.onWheel(event);
    this.onPointerDownBound = (event) => this.onPointerDown(event);
    this.onPointerMoveBound = (event) => this.onPointerMove(event);
    this.onPointerUpBound = (event) => this.onPointerUp(event);
    this.onPointerCancelBound = (event) => this.onPointerUp(event);
    this.onKeyDownBound = (event) => this.onKeyDown(event);
    this.onKeyUpBound = (event) => this.onKeyUp(event);

    this.attach();
    this.fitScale = this.scale;
    this.minScale = this.fitScale;
    this.applyTransform();
  }

  get isPinching(): boolean {
    return this.pinchActive;
  }

  get isPanning(): boolean {
    return this.panningBySpace;
  }

  canZoom(): boolean {
    return this.maxScale > this.minScale + 1e-4;
  }

  getZoomPercent(): number {
    if (this.minScale <= 0) return 100;
    return Math.round((this.scale / this.minScale) * 100);
  }

  zoomIn(step = 1.12): void {
    if (!this.canZoom()) return;
    const rect = this.adapter.getBaseRectBox();
    if (!rect) return;
    const anchorX = rect.x + rect.w * 0.5;
    const anchorY = rect.y + rect.h * 0.5;
    const targetScale = this.clampScale(this.scale * step);
    this.zoomAt(anchorX, anchorY, targetScale);
  }

  zoomOut(step = 1.12): void {
    if (!this.canZoom()) return;
    const rect = this.adapter.getBaseRectBox();
    if (!rect) return;
    const anchorX = rect.x + rect.w * 0.5;
    const anchorY = rect.y + rect.h * 0.5;
    const targetScale = this.clampScale(this.scale / step);
    this.zoomAt(anchorX, anchorY, targetScale);
  }

  resetZoom(): void {
    if (!this.canZoom()) return;
    const rect = this.adapter.getBaseRectBox();
    if (!rect) return;
    const anchorX = rect.x + rect.w * 0.5;
    const anchorY = rect.y + rect.h * 0.5;
    this.zoomAt(anchorX, anchorY, this.minScale);
  }

  isInteractionBlocked(): boolean {
    return this.pinchActive || this.panningBySpace || this.singleFingerPanActive;
  }

  setPieceDragging(active: boolean): void {
    this.pieceDragging = active;
  }

  handleResize(): void {
    this.scale = this.clampScale(this.scale);
    this.clampOffsets();
    this.applyTransform();
  }

  destroy(): void {
    this.canvas.removeEventListener('wheel', this.onWheelBound);
    this.canvas.removeEventListener('pointerdown', this.onPointerDownBound);
    window.removeEventListener('pointermove', this.onPointerMoveBound);
    window.removeEventListener('pointerup', this.onPointerUpBound);
    window.removeEventListener('pointercancel', this.onPointerCancelBound);
    window.removeEventListener('keydown', this.onKeyDownBound);
    window.removeEventListener('keyup', this.onKeyUpBound);
    this.canvas.classList.remove('is-pan-mode');
    this.canvas.classList.remove('is-dragging');
    this.canvas.style.touchAction = this.previousTouchAction;
    this.pointers.clear();
    this.pinchActive = false;
    this.panningBySpace = false;
    this.singleFingerPanActive = false;
    this.singleFingerPanPointerId = null;
  }

  maybeShowOneTimeHint(difficulty: number): void {
    if (difficulty < 252) return;
    const key = 'puzzle-viewport-hint-shown-v1';
    try {
      if (window.localStorage.getItem(key) === '1') return;
      window.localStorage.setItem(key, '1');
    } catch {
      // ignore storage errors
    }
    const text =
      'ontouchstart' in window
        ? 'Pinch to zoom.'
        : 'Ctrl + Wheel to zoom. Hold Space to pan.';
    const hintEl = createTutorialHintDom(text);
    window.setTimeout(() => destroyTutorialHint(hintEl), 3000);
  }

  private attach(): void {
    this.previousTouchAction = this.canvas.style.touchAction;
    this.canvas.style.touchAction = 'none';
    this.canvas.addEventListener('wheel', this.onWheelBound, { passive: false });
    this.canvas.addEventListener('pointerdown', this.onPointerDownBound);
    window.addEventListener('pointermove', this.onPointerMoveBound, { passive: false });
    window.addEventListener('pointerup', this.onPointerUpBound);
    window.addEventListener('pointercancel', this.onPointerCancelBound);
    window.addEventListener('keydown', this.onKeyDownBound);
    window.addEventListener('keyup', this.onKeyUpBound);
  }

  private onWheel(event: WheelEvent): void {
    if (!event.ctrlKey) return;
    event.preventDefault();
    const anchor = this.eventToViewport(event.clientX, event.clientY);
    const factor = Math.exp((-event.deltaY * 0.001));
    const targetScale = this.clampScale(this.scale * factor);
    this.zoomAt(anchor.x, anchor.y, targetScale);
  }

  private onPointerDown(event: PointerEvent): void {
    if (event.target !== this.canvas) return;
    const p = this.eventToViewport(event.clientX, event.clientY);
    this.pointers.set(event.pointerId, p);

    if (this.spaceDown && event.pointerType !== 'touch' && event.button === 0 && !this.pieceDragging) {
      this.pointerDownForPan = true;
      this.panningBySpace = true;
      this.panLastX = p.x;
      this.panLastY = p.y;
      this.canvas.classList.add('is-pan-mode');
      this.canvas.classList.add('is-dragging');
      event.preventDefault();
      return;
    }

    if (this.pointers.size >= 2 && !this.pieceDragging) {
      this.beginPinch();
      this.singleFingerPanActive = false;
      this.singleFingerPanPointerId = null;
      return;
    }

    if (
      this.pointers.size === 1 &&
      !this.pieceDragging &&
      this.scale > this.minScale + 1e-4 &&
      this.isPointInsideBoardViewport(p.x, p.y) &&
      !this.adapter.hasDraggablePieceAtScreenPoint(p.x, p.y)
    ) {
      this.singleFingerPanActive = true;
      this.singleFingerPanPointerId = event.pointerId;
      this.panLastX = p.x;
      this.panLastY = p.y;
      event.preventDefault();
    }
  }

  private onPointerMove(event: PointerEvent): void {
    if (!this.pointers.has(event.pointerId)) return;
    const p = this.eventToViewport(event.clientX, event.clientY);
    this.pointers.set(event.pointerId, p);

    if (this.panningBySpace && this.pointerDownForPan) {
      const dx = p.x - this.panLastX;
      const dy = p.y - this.panLastY;
      this.panLastX = p.x;
      this.panLastY = p.y;
      this.offsetX += dx;
      this.offsetY += dy;
      this.clampOffsets();
      this.applyTransform();
      event.preventDefault();
      return;
    }

    if (
      this.singleFingerPanActive &&
      this.singleFingerPanPointerId === event.pointerId &&
      this.pointers.size === 1 &&
      !this.pieceDragging
    ) {
      const dx = p.x - this.panLastX;
      const dy = p.y - this.panLastY;
      this.panLastX = p.x;
      this.panLastY = p.y;
      this.offsetX += dx;
      this.offsetY += dy;
      this.clampOffsets();
      this.applyTransform();
      event.preventDefault();
      return;
    }

    if (this.pinchActive && this.pointers.size >= 2 && !this.pieceDragging) {
      const { a, b } = this.firstTwoPointers();
      const distance = Phaser.Math.Distance.Between(a.x, a.y, b.x, b.y);
      if (this.pinchStartDistance <= 0 || distance <= 0) return;
      const targetScale = this.clampScale(this.pinchStartScale * (distance / this.pinchStartDistance));
      const midX = (a.x + b.x) * 0.5;
      const midY = (a.y + b.y) * 0.5;
      this.zoomAt(midX, midY, targetScale);
      event.preventDefault();
    }
  }

  private onPointerUp(event: PointerEvent): void {
    this.pointers.delete(event.pointerId);
    if (this.pointers.size < 2) this.pinchActive = false;
    if (this.singleFingerPanPointerId === event.pointerId) {
      this.singleFingerPanPointerId = null;
      this.singleFingerPanActive = false;
    }
    if (this.pointerDownForPan) {
      this.pointerDownForPan = false;
      this.panningBySpace = false;
      this.canvas.classList.remove('is-dragging');
      if (!this.spaceDown) this.canvas.classList.remove('is-pan-mode');
    }
  }

  private onKeyDown(event: KeyboardEvent): void {
    if (event.code !== 'Space') return;
    this.spaceDown = true;
    this.canvas.classList.add('is-pan-mode');
  }

  private onKeyUp(event: KeyboardEvent): void {
    if (event.code !== 'Space') return;
    this.spaceDown = false;
    if (!this.pointerDownForPan) {
      this.canvas.classList.remove('is-pan-mode');
      this.canvas.classList.remove('is-dragging');
      this.panningBySpace = false;
    }
  }

  private beginPinch(): void {
    const { a, b } = this.firstTwoPointers();
    this.pinchStartDistance = Phaser.Math.Distance.Between(a.x, a.y, b.x, b.y);
    this.pinchStartScale = this.scale;
    this.pinchActive = true;
    this.pointerDownForPan = false;
    this.panningBySpace = false;
    this.canvas.classList.remove('is-dragging');
  }

  private firstTwoPointers(): { a: PointerInfo; b: PointerInfo } {
    const values = [...this.pointers.values()];
    return {
      a: values[0] ?? { x: 0, y: 0 },
      b: values[1] ?? { x: 0, y: 0 },
    };
  }

  private eventToViewport(clientX: number, clientY: number): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    const cam = this.scene.cameras.main;
    const x = rect.width > 0 ? ((clientX - rect.left) * cam.width) / rect.width : clientX;
    const y = rect.height > 0 ? ((clientY - rect.top) * cam.height) / rect.height : clientY;
    return { x, y };
  }

  private clampScale(value: number): number {
    return Phaser.Math.Clamp(value, this.minScale, this.maxScale);
  }

  private isPointInsideBoardViewport(x: number, y: number): boolean {
    const r = this.adapter.getRectBox();
    if (!r) return false;
    return x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;
  }

  private zoomAt(anchorX: number, anchorY: number, newScale: number): void {
    const oldScale = this.scale;
    const worldX = (anchorX - this.offsetX) / oldScale;
    const worldY = (anchorY - this.offsetY) / oldScale;
    this.scale = newScale;
    this.offsetX = anchorX - worldX * newScale;
    this.offsetY = anchorY - worldY * newScale;
    this.clampOffsets();
    this.applyTransform();
  }

  private clampOffsets(): void {
    const rect = this.adapter.getBaseRectBox();
    if (!rect) return;
    const viewportX = rect.x;
    const viewportY = rect.y;
    const viewportW = rect.w;
    const viewportH = rect.h;
    const contentX = rect.x;
    const contentY = rect.y;
    const contentW = rect.w;
    const contentH = rect.h;
    const scaledW = contentW * this.scale;
    const scaledH = contentH * this.scale;

    if (scaledW <= viewportW) {
      this.offsetX = viewportX + (viewportW - scaledW) * 0.5 - contentX * this.scale;
    } else {
      const minX = viewportX + viewportW - (contentX + contentW) * this.scale;
      const maxX = viewportX - contentX * this.scale;
      this.offsetX = Phaser.Math.Clamp(this.offsetX, minX - this.clampMargin, maxX + this.clampMargin);
    }
    if (scaledH <= viewportH) {
      this.offsetY = viewportY + (viewportH - scaledH) * 0.5 - contentY * this.scale;
    } else {
      const minY = viewportY + viewportH - (contentY + contentH) * this.scale;
      const maxY = viewportY - contentY * this.scale;
      this.offsetY = Phaser.Math.Clamp(this.offsetY, minY - this.clampMargin, maxY + this.clampMargin);
    }
  }

  private applyTransform(): void {
    this.adapter.setViewportTransform(this.scale, this.offsetX, this.offsetY);
    const percent = this.getZoomPercent();
    if (percent !== this.lastReportedZoomPercent) {
      this.lastReportedZoomPercent = percent;
      this.onZoomPercentChange?.(percent);
    }
  }
}
